/**
 * CreditBalanceCard — lead/client Billing-tab card surfacing the
 * `lead.creditBalanceCents` denormalized balance and the latest ledger entries.
 *
 * iter_223, Phase 1.
 *
 * Props
 *   companyId  : str   — required
 *   leadId     : str   — required
 *
 * Behavior
 *   - Hides itself when balance == 0 AND there are no ledger entries.
 *   - Owner/admin sees an "Adjust Credit" button that opens AdjustCreditModal.
 *   - Polls /api/leads/{id}/credits on mount and after every successful adjust.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { auth } from '../../firebase';
import { Coins, Plus, Minus, RotateCcw, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import AdjustCreditModal from '../AdjustCreditModal';
import { useRole } from '../../context/RoleContext';

const fmtCents = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmtDate  = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric',
    });
  } catch (e) { return ''; }
};

const SOURCE_META = {
  manual_add:          { label: 'Manual add',         cls: 'text-green-700', Icon: Plus },
  manual_subtract:     { label: 'Manual subtract',    cls: 'text-orange-700', Icon: Minus },
  invoice_application: { label: 'Applied to invoice', cls: 'text-blue-700',  Icon: Minus },
  invoice_restoration: { label: 'Refund recovery',    cls: 'text-emerald-700', Icon: RotateCcw },
};

const CreditBalanceCard = ({ companyId, leadId }) => {
  const { isOwner, isAdmin } = useRole();
  const canAdjust = isOwner || isAdmin;
  const [summary, setSummary] = useState(null);   // { creditBalanceCents, recentEntries, ... }
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!companyId || !leadId) return;
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const qs    = new URLSearchParams({ companyId, limit: '5' });
      const res   = await fetch(
        `${backendUrl}/api/leads/${leadId}/credits?${qs}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (res.ok) {
        setSummary(await res.json());
      }
    } catch (err) {
      console.error('[CreditBalanceCard] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId, leadId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Hide card entirely when there's no balance + no history (clean lead).
  const balance = summary?.creditBalanceCents || 0;
  const entries = summary?.recentEntries || [];
  if (!loading && balance === 0 && entries.length === 0 && !canAdjust) {
    return null;
  }

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-sm"
      data-testid="credit-balance-card"
    >
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Coins className="h-4 w-4 text-indigo-600" />
          <span className="text-sm font-semibold text-gray-900">Account Credit</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            onClick={fetchSummary}
            disabled={loading}
            data-testid="credit-card-refresh-btn"
            title="Refresh balance"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {canAdjust && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-400 font-medium"
              onClick={() => setModalOpen(true)}
              data-testid="adjust-credit-btn"
            >
              Adjust Credit
            </Button>
          )}
        </div>
      </div>

      {/* Balance + lifetime numbers */}
      <div className="px-4 py-3 flex items-baseline justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
            Balance
          </p>
          <p
            className="text-2xl font-bold text-indigo-700 tabular-nums"
            data-testid="credit-balance-display"
          >
            {fmtCents(balance)}
          </p>
        </div>
        <div className="text-right text-[11px] text-gray-500">
          <p>
            Lifetime added{' '}
            <span className="text-gray-700 font-medium">
              {fmtCents(summary?.creditLifetimeAddedCents || 0)}
            </span>
          </p>
          <p>
            Lifetime used{' '}
            <span className="text-gray-700 font-medium">
              {fmtCents(summary?.creditLifetimeUsedCents || 0)}
            </span>
          </p>
        </div>
      </div>

      {/* Recent ledger entries */}
      {entries.length > 0 && (
        <div
          className="border-t border-gray-100 px-4 py-2.5"
          data-testid="credit-ledger-list"
        >
          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-medium mb-1.5">
            Recent activity
          </p>
          <div className="space-y-1.5">
            {entries.map((e) => {
              const meta = SOURCE_META[e.source] || { label: e.source, cls: 'text-gray-700', Icon: Coins };
              const Icon = meta.Icon;
              const isPositive = (e.deltaCents || 0) > 0;
              return (
                <div
                  key={e.entryId}
                  className="flex items-start justify-between text-xs"
                  data-testid={`credit-ledger-entry-${e.entryId}`}
                >
                  <div className="flex items-start gap-1.5 min-w-0 flex-1">
                    <Icon className={`h-3 w-3 mt-0.5 shrink-0 ${meta.cls}`} />
                    <div className="min-w-0">
                      <p className="text-gray-700 truncate">{e.reason}</p>
                      <p className="text-[10px] text-gray-400">
                        {meta.label}
                        {e.actor?.email ? ` · ${e.actor.email}` : ''}
                        {e.createdAt ? ` · ${fmtDate(e.createdAt)}` : ''}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 ml-2 font-mono font-medium tabular-nums ${
                      isPositive ? 'text-green-700' : 'text-orange-700'
                    }`}
                  >
                    {isPositive ? '+' : '−'}{fmtCents(Math.abs(e.deltaCents))}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AdjustCreditModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        companyId={companyId}
        leadId={leadId}
        currentBalanceCents={balance}
        onSuccess={() => { setModalOpen(false); fetchSummary(); }}
      />
    </div>
  );
};

export default CreditBalanceCard;
